import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import BrandMark from '@/components/BrandMark';
import { useLang, buildLangPath, switchLangPath, FRENCH_PUBLIC_ENABLED, useT } from '@/lib/i18n';

const NAV = [
    { to: '/artists', key: 'nav.artists', label: 'Artists' },
    { to: '/artworks', key: 'nav.artworks', label: 'Artworks' },
    { to: '/exhibitions', key: 'nav.exhibitions', label: 'Exhibitions' },
    { to: '/about', key: 'nav.about', label: 'About' },
    { to: '/journal', key: 'nav.journal', label: 'Journal' },
    { to: '/film', key: 'nav.film', label: 'Film' },
    { to: '/contact', key: 'nav.contact', label: 'Contact' },
];

/* Routes that render a dark full-bleed hero — nav needs light text at top.
   Matches both English and French variants. */
const DARK_HERO_ROUTES = [
    /^\/(fr\/?)?$/,
    /^\/(fr\/)?private-viewing$/,
    /^\/(fr\/)?exhibitions\/[^/]+$/,
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const loc = useLocation();
    const navigate = useNavigate();
    const { lang } = useLang();
    const t = useT();

    const onDarkHero = DARK_HERO_ROUTES.some((re) => re.test(loc.pathname));
    const lightMode = onDarkHero && !scrolled;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

    const homePath = buildLangPath('/', lang);

    const handleLogoClick = (e) => {
        e.preventDefault();
        if (mobileOpen) setMobileOpen(false);
        const lenis = typeof window !== 'undefined' ? window.__lenis : null;
        if (loc.pathname === homePath) {
            /* Same-route click: native window.scrollTo is overwritten by
               Lenis on its next RAF tick, so drive Lenis directly. */
            if (lenis) {
                lenis.scrollTo(0, { duration: 1.2, force: true });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            /* Cross-route: ScrollToTop resets scroll via Lenis on mount. */
            navigate(homePath);
        }
    };

    const inkColor = lightMode ? 'var(--bg)' : 'var(--ink)';
    const subColor = lightMode ? 'rgba(249,248,246,0.75)' : 'var(--ink-2)';

    /* Language switcher — path to the equivalent page in the opposite language */
    const otherLang = lang === 'fr' ? 'en' : 'fr';
    const otherLangPath = switchLangPath(loc.pathname, otherLang);

    return (
        <header
            data-testid="site-nav"
            className="fixed top-0 left-0 right-0 z-50 transition-all duration-700"
            style={{
                background: scrolled ? 'rgba(236,229,211,0.94)' : 'transparent',
                backdropFilter: scrolled ? 'blur(20px) saturate(140%)' : 'none',
                borderBottom: scrolled ? '1px solid var(--rule-soft)' : '1px solid transparent',
            }}
        >
            <div className="max-w-[1600px] mx-auto px-8 md:px-16 flex items-center justify-between" style={{ height: scrolled ? 80 : 112, transition: 'height 0.6s cubic-bezier(0.16,1,0.3,1)' }}>
                <Link
                    to={homePath}
                    data-testid="nav-logo"
                    onClick={handleLogoClick}
                    aria-label={t('nav.home_aria')}
                    className="flex flex-col leading-none relative z-10"
                >
                    <BrandMark
                        variant={lightMode ? 'light' : 'dark'}
                        size={scrolled ? 32 : 40}
                        inkColor={inkColor}
                        subColor={subColor}
                        wordmarkSize={26}
                        sublineSize={9}
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-12">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={buildLangPath(n.to, lang)}
                            data-testid={`nav-link-${n.label.toLowerCase()}`}
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            style={{ color: inkColor, transition: 'color .6s, letter-spacing .7s' }}
                        >
                            {t(n.key)}
                        </NavLink>
                    ))}
                    {/* Language switcher — text-only, matches nav-link typography.
                       Hidden entirely while French is not publicly enabled. */}
                    {FRENCH_PUBLIC_ENABLED && (
                        <span
                            data-testid="nav-lang-switcher"
                            aria-label="Language"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                            <Link
                                to={lang === 'en' ? loc.pathname : switchLangPath(loc.pathname, 'en')}
                                data-testid="nav-lang-en"
                                aria-current={lang === 'en' ? 'true' : undefined}
                                className="nav-link"
                                style={{
                                    color: inkColor,
                                    opacity: lang === 'en' ? 1 : 0.55,
                                    transition: 'opacity .6s, color .6s',
                                }}
                            >
                                EN
                            </Link>
                            <span aria-hidden="true" style={{ color: inkColor, opacity: 0.4 }}>/</span>
                            <Link
                                to={lang === 'fr' ? loc.pathname : switchLangPath(loc.pathname, 'fr')}
                                data-testid="nav-lang-fr"
                                aria-current={lang === 'fr' ? 'true' : undefined}
                                className="nav-link"
                                style={{
                                    color: inkColor,
                                    opacity: lang === 'fr' ? 1 : 0.55,
                                    transition: 'opacity .6s, color .6s',
                                }}
                            >
                                FR
                            </Link>
                        </span>
                    )}
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    className="lg:hidden flex flex-col gap-1.5 p-2"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    aria-label={t('nav.menu_aria')}
                >
                    <span style={{ width: 22, height: 1, background: inkColor, transition: 'transform .3s, background .6s', transform: mobileOpen ? 'rotate(45deg) translate(4px,4px)' : 'none' }} />
                    <span style={{ width: 22, height: 1, background: inkColor, opacity: mobileOpen ? 0 : 1, transition: 'opacity .3s, background .6s' }} />
                    <span style={{ width: 22, height: 1, background: inkColor, transition: 'transform .3s, background .6s', transform: mobileOpen ? 'rotate(-45deg) translate(4px,-4px)' : 'none' }} />
                </button>
            </div>

            {/* Mobile menu */}
            <div
                data-testid="nav-mobile-panel"
                className="lg:hidden overflow-hidden transition-all duration-700"
                style={{
                    maxHeight: mobileOpen ? '100vh' : 0,
                    background: 'var(--bg)',
                    borderTop: mobileOpen ? '1px solid var(--rule-soft)' : 'none',
                }}
            >
                <div className="px-8 py-12 flex flex-col gap-8">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={buildLangPath(n.to, lang)}
                            data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontStyle: 'italic',
                                fontWeight: 500,
                                fontSize: 32,
                                color: 'var(--ink)',
                                letterSpacing: '-0.015em',
                            }}
                        >
                            {t(n.key)}
                        </NavLink>
                    ))}
                    {/* Mobile language switcher — hidden entirely while French
                       is not publicly enabled. */}
                    {FRENCH_PUBLIC_ENABLED && (
                        <div
                            data-testid="nav-mobile-lang-switcher"
                            aria-label="Language"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                marginTop: 12,
                                paddingTop: 24,
                                borderTop: '1px solid var(--rule-soft)',
                            }}
                        >
                            <Link
                                to={otherLangPath}
                                onClick={() => {}}
                                data-testid="nav-mobile-lang-en"
                                aria-current={lang === 'en' ? 'true' : undefined}
                                className="label"
                                style={{ color: 'var(--ink-2)', opacity: lang === 'en' ? 1 : 0.55 }}
                            >
                                EN
                            </Link>
                            <span aria-hidden="true" style={{ color: 'var(--ink-2)', opacity: 0.4 }}>/</span>
                            <Link
                                to={lang === 'fr' ? loc.pathname : switchLangPath(loc.pathname, 'fr')}
                                data-testid="nav-mobile-lang-fr"
                                aria-current={lang === 'fr' ? 'true' : undefined}
                                className="label"
                                style={{ color: 'var(--ink-2)', opacity: lang === 'fr' ? 1 : 0.55 }}
                            >
                                FR
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}
